import { ActionBus, Secret } from 'game-logic';
import {
  BehaviorSubject,
  interval,
  map,
  Observable,
  startWith,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import { GameBehavior, GameBehaviorActions } from './game-behavior';
import { effect, EffectRef, runInInjectionContext, Signal } from '@angular/core';
import { RoomService } from 'src/app/core/services/room/room.service';
import { EventName } from 'src/app/core/services/analytics/event-names';
import { ApiResponseStartRound, WagerApi } from 'src/app/core/api/wager-api';
import { Api } from 'src/app/core/api/api';
import { CloudError } from 'src/app/shared/errors/cloud-error';
import { HttpErrorResponse } from '@angular/common/http';
import { PromiseHolder } from 'src/app/shared/utils/promise-holder';
import { ApiResponseSecretsDisclosed, DealerApi } from 'src/app/core/api/dealer-api';
import { Stopwatch } from 'src/app/shared/utils/stopwatch';
import { Room } from 'src/app/model/room/room';
import { RoomModel } from 'src/app/model/room/room.model';
import { GameId } from 'src/app/model/game/game-id';
import { Round } from 'src/app/shared/types/round';
import { UxGameServices } from 'src/app/core/services/ux-game/ux-game-services';
import { GameAlert } from '../common/game-alert';

export class PaidBehavior implements GameBehavior {
  get logicVersion(): number {
    return this.room?.logicVersion() ?? 1;
  }
  get currentRoomId(): string {
    return this.roomId;
  }
  get roundDeadline(): number {
    return this.deadline || 60000;
  }
  get behaviorActions(): ActionBus<GameBehaviorActions> {
    return this.behaviorActionBus;
  }
  get formattedReward$(): Observable<string | undefined> {
    return this.formattedReward.asObservable();
  }
  get opponent$(): Observable<string | undefined> {
    return this.opponent.asObservable();
  }
  public get opponentId$(): Observable<string | undefined> {
    return this.opponentId.asObservable();
  }
  public get bountyAmount$(): Observable<number | undefined> {
    return this.bountyAmount.asObservable();
  }

  private behaviorActionBus = new ActionBus<GameBehaviorActions>();
  private formattedReward = new BehaviorSubject<string | undefined>(undefined);
  private opponent = new BehaviorSubject<string | undefined>(undefined);
  private opponentId = new BehaviorSubject<string | undefined>(undefined);
  private bountyAmount = new BehaviorSubject<number | undefined>(undefined);
  private destroy$ = new Subject<void>();

  private dealId?: string;
  private dealerUrl?: string;
  private room: Room | null | undefined;
  private deadline: number | undefined;
  private tournamentRoundDeadline: number | undefined;
  private timerSubscription: Subscription | null = null; // To track the active timer
  private tournamentTimerSubscription: Subscription | null = null; // To track the active timer
  private roomSignal: Signal<Room | null | undefined>;
  private effectRef: EffectRef | undefined;

  constructor(
    private roomId: string,
    private services: UxGameServices,
    private gameAlert: GameAlert,
  ) {
    this.roomSignal = services.roomService.getSignal(this.roomId, services.profileService.id);
  }

  // --------------

  public async initRoom(): Promise<GameId> {
    this.cleanupEffect();
    const job = this.services.jobService.create(async () => {
      return await this.fetchRoom();
    });
    job.showNoInternetRetry().showSpinner();
    job.addErrorHandler().showFatalError().reloadWindow();
    const room = await job.run();
    this.setRoom(room);
    return room.gameId();
  }

  async initRoomTournament(): Promise<void> {
    const job = this.services.tournamentService.getRoomTournaments(this.roomId);
    job.showNoInternetRetry().showSpinner();
    job.addErrorHandler().showFatalError().reloadWindow();
    const roomTournaments = await job.run();

    if (roomTournaments?.data && roomTournaments.data.length > 0) {
      const tournament = roomTournaments.data[0];
      this.tournamentRoundDeadline = tournament.roundDeadline;
    }
  }

  public async createGame(): Promise<void> {
    if (this.room?.model.roundState && this.room?.model.roundState !== Round.NotStarted) {
      // If the round has already started, load the game as normal, and hide the start button
      this.deadline = this.room?.model.roundDeadline || 60000;
      this.startTimeout(this.deadline);
      this.startTournamentTimer(this.deadline);
      this.behaviorActionBus.emit('showStartButton', { show: false });
      this.behaviorActionBus.emit('resumeExistingGame');
    } else {
      this.behaviorActionBus.emit('showStartButton', { show: false });
      this.behaviorActionBus.emit('autoStartGame');
      this.services.roomListService.addRoom(this.roomId, this.services.profileService.id);
    }

    runInInjectionContext(this.services.injector, () => {
      this.effectRef = effect(() => {
        const room = this.roomSignal();
        if (room) {
          if (room.model !== null) {
            const roundDeadline = room.model.roundDeadline ?? 0;
            if (roundDeadline > 0 && room.model.roundState === Round.Started) {
              this.deadline = roundDeadline;
              this.startTimeout(roundDeadline);
              this.startTournamentTimer(roundDeadline);
            }
            this.updateRoom(room);
          }
        }
      });
    });
  }

  // --------------

  public async startRoom(): Promise<void> {
    this.services.analyticsService.log(EventName.ClinchGameStartRoom, {
      roomId: this.roomId,
    });

    const job = this.services.jobService.create<ApiResponseStartRound>(async () => {
      return await WagerApi.request(this.services.httpService).startRound(this.roomId);
    });

    job.addErrorHandler().showFatalError().reloadWindow();
    const round = await job.runNoThrow();

    if (round) {
      this.dealId = round.data.dealId;
      this.dealerUrl = round.data.dealerUrl;
    }
  }

  public onDestroy(): void {
    this.stopTimeout();
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupEffect();
    this.stopTournamentTimer();
  }

  public async onStartNewGame(): Promise<void> {
    const job = this.services.jobService.create(async () => {
      const room = await this.fetchRoom();
      this.setRoom(room);
    });
    job.showNoInternetRetry().showSpinner();
    job.addErrorHandler().showFatalError().reloadWindow();
    await job.run();
  }

  public onPauseGame(pause: boolean): void {
    if (pause) {
      this.stopTimeout();
      this.stopTournamentTimer();
      return;
    }
    this.startTimeout(this.deadline ?? 0);
    this.startTournamentTimer(this.deadline ?? 0);
  }

  public onStopGame(): void {
    this.stopTimeout();
    this.stopTournamentTimer();
  }

  public async getInitialMoves(): Promise<string[]> {
    const job = this.services.jobService.create(async () => {
      return await this.services.movesService.getInitialMoves(
        this.roomId,
        this.services.profileService.id,
      );
    });

    job.addErrorHandler().showFatalError().reloadWindow();
    const moves = await job.runNoThrow();
    return moves ?? [];
  }

  public getMoves(): string[] {
    return this.services.movesService.getMoves(this.roomId, this.services.profileService.id);
  }

  public async setMoves(moves: string[]): Promise<boolean> {
    try {
      const job = this.services.jobService.create(async () => {
        await this.services.movesService.setMoves(
          this.roomId,
          this.services.profileService.id,
          moves,
        );
      });
      job.addErrorHandler().onError(async (_error: unknown) => {
        const isNoInternet = Api.isNoInternetError(_error);
        await this.services.analyticsService.log(EventName.SetMovesError, {
          roomId: this.roomId,
          isNoInternet,
        });
      });
      await job.run();
      return true;
    } catch (error) {
      console.error('Error: Moves set failed');
      return false;
    }
  }

  public async discloseSecrets(
    indexes: number[],
    gameHash: string,
    disclosedSecrets: number,
    existingDeal: boolean,
  ): Promise<Secret[]> {
    this.services.analyticsService.log(EventName.ClinchGameDiscloseSecrets);

    if (this.dealId === undefined || this.dealerUrl === undefined) {
      this.gameAlert?.fatal(this.roomId);
      return new Promise((_resolve, _reject) => {
        // Intentionally empty: resolve or reject is never called
      });
    }

    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        const response = await this.disclosedSecret(
          this.dealId,
          this.dealerUrl,
          indexes,
          gameHash,
          disclosedSecrets,
          existingDeal,
        );

        const missingIndices = this.missingIndices(indexes, response);

        if (missingIndices.length === 0) {
          return response.data.secrets;
        } else if (existingDeal) {
          // Disclose new secrets
          const newIndices = missingIndices;

          const newResponse = await this.disclosedSecret(
            this.dealId,
            this.dealerUrl,
            newIndices,
            gameHash,
            disclosedSecrets,
            false,
          );

          if (this.missingIndices(newIndices, newResponse).length === 0) {
            return [...response.data.secrets, ...newResponse.data.secrets];
          }
        }

        this.gameAlert?.fatal(this.roomId);
        return Promise.resolve([]);
      } catch (error) {
        await this.services.analyticsService.handleError(
          error,
          EventName.ClinchGameDiscloseSecretsFailed,
        );

        if (error instanceof CloudError) {
          if (
            error.code === 'disclosed_secrets_mismatch' ||
            error.code === 'game_state_hash_mismatch'
          ) {
            await this.gameAlert.secretsMismatch();
            return Promise.resolve([]);
          }
        } else if (error instanceof HttpErrorResponse && error.status === 400) {
          this.gameAlert?.fatal(this.roomId);
          return Promise.resolve([]);
        }

        const retryClicked = new PromiseHolder<void>();
        retryClicked.init();
        this.gameAlert?.reconnect(retryClicked);
        await retryClicked.getPromise();
      }
    }
  }

  private missingIndices(indexes: number[], response: ApiResponseSecretsDisclosed) {
    const responseIndices = new Set(response.data.secrets.map((secret) => secret.index));
    return indexes.filter((index) => !responseIndices.has(index));
  }

  private startTimeout(roundDeadline: number) {
    this.stopTimeout();

    this.timerSubscription = interval(1000)
      .pipe(
        startWith(0),
        map(() => Stopwatch.toSecs(roundDeadline - Date.now())),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (remaining) => {
          if (remaining <= 0) {
            this.behaviorActionBus.emit('onCountdownChanged', { countdown: 0 });
            this.behaviorActionBus.emit('onDeadlineExpired');
            this.timerSubscription?.unsubscribe(); // Manually complete
          } else {
            this.behaviorActionBus.emit('onCountdownChanged', { countdown: remaining });
          }
        },
      });
  }

  private stopTimeout() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe(); // Unsubscribe from the current timer
      this.timerSubscription = null;
    }
  }

  private startTournamentTimer(roomRoundDeadline: number) {
    if (
      roomRoundDeadline &&
      this.tournamentRoundDeadline &&
      this.tournamentRoundDeadline > 0 &&
      this.tournamentRoundDeadline < roomRoundDeadline
    ) {
      this.stopTournamentTimer();
      this.tournamentTimerSubscription = interval(1000)
        .pipe(
          startWith(0),
          map(() => Stopwatch.toSecs((this.tournamentRoundDeadline ?? 0) - Date.now())),
          takeUntil(this.destroy$),
        )
        .subscribe({
          next: (remaining) => {
            if (remaining < 0) {
              this.tournamentTimerSubscription?.unsubscribe();
            } else {
              this.behaviorActionBus.emit('onTournamentCountdownChanged', {
                tournamentCountdown: remaining,
              });
            }
          },
        });
    }
  }

  private stopTournamentTimer() {
    if (this.tournamentTimerSubscription) {
      this.tournamentTimerSubscription?.unsubscribe();
      this.tournamentTimerSubscription = null;
    }
  }

  private setRoom(room: Room) {
    this.room = room;

    this.formattedReward.next(this.room.formattedReward(this.services.currencyPipe));
    this.opponent.next(this.room.opponent());
    this.opponentId.next(this.room.opponentId());
    this.bountyAmount.next(this.room.bountyAmount());
  }

  private updateRoom(room: Room) {
    if (this.room !== undefined) {
      this.setRoom(room);
    }
  }

  private async fetchRoom(): Promise<Room> {
    const roomModel = (await WagerApi.request(this.services.httpService).rooms(this.roomId))
      .data as RoomModel;
    const roomPath = RoomService.roomPath(this.roomId, this.services.profileService.id);
    return new Room(roomModel, this.roomId, roomPath);
  }

  private cleanupEffect() {
    if (this.effectRef) {
      this.effectRef.destroy();
      this.effectRef = undefined;
    }
  }

  private async disclosedSecret(
    dealId: string,
    dealerUrl: string,
    indexes: number[],
    gameStateHash: string,
    disclosedSecrets: number,
    existingDeal: boolean,
  ): Promise<ApiResponseSecretsDisclosed> {
    if (existingDeal) {
      const response = await DealerApi.request(
        dealerUrl,
        this.services.httpService,
      ).existingSecrets(dealId);
      return {
        data: {
          secrets: response.data.disclosures,
        },
      };
    }
    return DealerApi.request(dealerUrl, this.services.httpService).disclose(
      dealId,
      indexes,
      gameStateHash,
      disclosedSecrets,
    );
  }
}
